import EmojiPicker from 'emoji-picker-react';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { useDispatch } from 'react-redux';
import {
  Button,
  FieldTextInput,
  Form,
  IconCollection,
  IconSpinner,
  ImageFromFile,
  OutsideClickHandler,
  ResponsiveImage,
} from '../../../../components';
import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import { isValidDocumentFile } from '../../../../components/DragAndDrop/DragAndDrop';
import FileUploadModal from '../CreatePostForm/FileUploadModal';
import GifUploadModal from '../CreatePostForm/GifUploadModal';
import ImageUploadModal from '../CreatePostForm/ImageUploadModal';
import VideoUploadModal from '../CreatePostForm/VideoUploadModal';
import css from './CircleFeedModal.module.css';
import { abbreviateName, handleFileUpload } from '../../../../util/circleHelper';
import { convertToRaw, RichUtils } from 'draft-js';
import CircleBlockEditor from '../../../../components/CircleBlockEditor/CircleBlockEditor';
import EditorOptionDropdown from '../CreatePostForm/EditorOptionDropdown';
import { FormattedMessage } from '../../../../util/reactIntl';
import { UNORDER_LIST } from '../../../../util/types';
import useActualNetworkStatus from '../../../../hooks/useActualNetworkStatus';
import {
  generateVideoThumbnail,
  updateUploadedVideoPoster,
} from '../CreatePostForm/VideoUploadModal';
import {
  getPostMediaCount,
  getPostMediaKind,
  validatePostMediaFiles,
} from '../CreatePostForm/postMediaUpload';

const COMMENT_MEDIA_VALUE_FIELDS = [
  'image',
  'video',
  'document',
  'gifs',
  'imagefiles',
  'videofiles',
  'documentsfiles',
  'giffiles',
];

const hasCommentMediaValues = values =>
  COMMENT_MEDIA_VALUE_FIELDS.some(field => {
    const fieldValue = values?.[field];
    return Array.isArray(fieldValue) && fieldValue.length > 0;
  });

const AddCommentForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        form,
        handleSubmit,
        onChange,
        inProgress,
        invalid,
        pristine,
        submitting,
        requestCreateComment,
        onEmojiClick,
        isEmojiOpen,
        values,
        requestDirectUpload = false,
        onGiphyChange,
        onDocumentClick,
        onImageClick,
        onVideoClick,
        isGiphyOpen,
        isDocumentModal,
        isImageModal,
        isVideoUpload,
        avatar_url,
        name,
        advancedSearchData,
        onAdvancedSearch,
        current_avatar_url,
        currentUserName,
        isReplyComment,
        editorState,
        setEditorState,
        insertEmoji,
        replyCommentPlaceHolder,
        handleCloseFeedModal = () => {},
        onSetElephantVerificationModal = () => {},
        currentUser,
        currentUserHasActiveSubscription = false,
        currentUserHasStripeConnected = false,
        isEditMode = false,
        editInitialValues = {},
        editFormKey = null,
        onCancelEdit = () => {},
        submitLabel = 'Post',
      } = fieldRenderProps;

      const [isEditorOptionOpen, setIsEditorOptionOpen] = useState(false);
      const [isEdited, setIsEdited] = useState(false);
      const [limitExceed, setLimitExceed] = useState(false);
      const { isOnline } = useActualNetworkStatus();
      const [submitError, setSubmitError] = useState(null);
      const [dropUploadError, setDropUploadError] = useState(null);
      const [isDropUploading, setIsDropUploading] = useState(false);
      const [isDragActive, setIsDragActive] = useState(false);
      // Final Form's `submitting` state updates after the submit event. Keep a
      // synchronous guard as well so two rapid clicks cannot start two requests.
      const submitInProgressRef = useRef(false);
      const dragCounterRef = useRef(0);
      const editInitializationRef = useRef({ key: null, hasMedia: false });
      const dispatch = useDispatch();

      useEffect(() => {
        if (!isEditMode) {
          editInitializationRef.current = { key: null, hasMedia: false };
          return;
        }

        if (!form?.initialize) {
          return;
        }

        const currentEditKey = editFormKey || 'edit-comment';
        const nextInitialValues = editInitialValues || {};
        const nextHasMedia = hasCommentMediaValues(nextInitialValues);
        const currentValues = form.getState?.().values || {};
        const currentHasMedia = hasCommentMediaValues(currentValues);
        const initializedEdit = editInitializationRef.current;
        const isNewEditTarget = initializedEdit.key !== currentEditKey;

        if (isNewEditTarget) {
          form.initialize(nextInitialValues);
          editInitializationRef.current = {
            key: currentEditKey,
            hasMedia: nextHasMedia || currentHasMedia,
          };
          return;
        }

        if (!initializedEdit.hasMedia && nextHasMedia) {
          if (!currentHasMedia) {
            form.initialize(nextInitialValues);
          }
          editInitializationRef.current = { key: currentEditKey, hasMedia: true };
        }
      }, [editFormKey, editInitialValues, form, isEditMode]);

      const handleEditorChange = newEditorState => {
        setEditorState(newEditorState);
        // Convert editor content to raw format and check if there's meaningful text
        const content = convertToRaw(newEditorState.getCurrentContent());
        const hasText = content.blocks.some(block => block.text.trim().length > 0);

        setIsEdited(hasText);
      };

      const hasMediaFiles =
        values?.imagefiles?.length > 0 ||
        values?.giffiles?.length > 0 ||
        values?.videofiles?.length > 0 ||
        values?.documentsfiles?.length > 0;

      const isButtonDisabled = !isEditMode && !isEdited && !hasMediaFiles;

      const handleEmoji = emoji => {
        insertEmoji(emoji.emoji);
        setIsEdited(true);
      };

      const toggleDropdown = () => {
        setIsEditorOptionOpen(prev => !prev);
      };

      const toggleBlockType = blockType => {
        setEditorState(RichUtils.toggleBlockType(editorState, blockType));
      };

      const toggleInlineStyle = inlineStyle => {
        // Simply toggle the inline style at the current cursor position
        // This will apply to future typed text without selecting existing text
        setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
      };

      const getCurrentBlockType = () => {
        const selection = editorState.getSelection();
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(selection.getStartKey());
        return block.getType();
      };

      const getCurrentInlineStyle = () => {
        return editorState.getCurrentInlineStyle();
      };

      const currentBlockType = getCurrentBlockType();
      const currentInlineStyle = getCurrentInlineStyle();

      const commentPlaceHolder = <FormattedMessage id="CircleCommunity.commentPlaceholder" />;
      const isUploadDisabled =
        requestDirectUpload || requestCreateComment || isDropUploading || !isOnline;

      const commitMediaUploads = successfulUploads => {
        const imageUploads = successfulUploads.filter(upload => upload.kind === 'image');
        const videoUploads = successfulUploads
          .map(upload => {
            if (upload.kind !== 'video') return upload;
            return {
              ...upload,
              previewUrl: URL.createObjectURL(upload.file),
            };
          })
          .filter(upload => upload.kind === 'video');

        const currentValues = form.getState?.().values || values || {};
        const newImages = imageUploads.map(upload => upload.response);
        const newImageFiles = imageUploads.map(upload => ({
          file: upload.file,
          signed_id: upload.response.signed_id,
        }));
        const newVideos = videoUploads.map(upload => upload.response);
        const newVideoFiles = videoUploads.map(upload => ({
          file: upload.previewUrl,
          poster: null,
          signed_id: upload.response.signed_id,
        }));

        form.change('image', [...(currentValues.image || []), ...newImages]);
        form.change('imagefiles', [...(currentValues.imagefiles || []), ...newImageFiles]);
        form.change('video', [...(currentValues.video || []), ...newVideos]);
        form.change('videofiles', [...(currentValues.videofiles || []), ...newVideoFiles]);

        videoUploads.forEach(upload => {
          generateVideoThumbnail(upload.previewUrl)
            .then(poster =>
              updateUploadedVideoPoster({
                form,
                signedId: upload.response.signed_id,
                poster,
              })
            )
            .catch(error => console.error('Video thumbnail generation failed:', error));
        });
      };

      const uploadDroppedMedia = async mediaFiles => {
        if (!mediaFiles.length) {
          return [];
        }

        const currentValues = form.getState?.().values || values || {};
        const { acceptedFiles, errors: validationErrors } = validatePostMediaFiles({
          files: mediaFiles,
          existingCount: getPostMediaCount(currentValues),
        });

        const errors = [...validationErrors];
        if (!acceptedFiles.length) {
          return errors;
        }

        const results = await Promise.allSettled(
          acceptedFiles.map(file => handleFileUpload(file, dispatch))
        );
        const successfulUploads = [];
        const failedFiles = [];

        results.forEach((result, index) => {
          const file = acceptedFiles[index];
          if (result.status === 'fulfilled' && result.value?.signed_id) {
            successfulUploads.push({
              file,
              kind: getPostMediaKind(file),
              response: result.value,
            });
          } else {
            failedFiles.push(file.name);
          }
        });

        if (successfulUploads.length) {
          commitMediaUploads(successfulUploads);
        }

        if (failedFiles.length) {
          errors.push(`Failed to upload ${failedFiles.length} file(s): ${failedFiles.join(', ')}.`);
        }

        return errors;
      };

      const uploadDroppedDocuments = async documentFiles => {
        if (!documentFiles.length) {
          return [];
        }

        const results = await Promise.allSettled(
          documentFiles.map(file => handleFileUpload(file, dispatch))
        );
        const successfulDocuments = [];
        const failedFiles = [];

        results.forEach((result, index) => {
          const file = documentFiles[index];
          if (result.status === 'fulfilled' && result.value?.signed_id) {
            successfulDocuments.push({
              file,
              response: result.value,
            });
          } else {
            failedFiles.push(file.name);
          }
        });

        if (successfulDocuments.length) {
          const currentValues = form.getState?.().values || values || {};
          form.change('document', [
            ...(currentValues.document || []),
            ...successfulDocuments.map(upload => upload.response),
          ]);
          form.change('documentsfiles', [
            ...(currentValues.documentsfiles || []),
            ...successfulDocuments.map(upload => ({
              file: upload.file,
              signed_id: upload.response.signed_id,
            })),
          ]);
        }

        return failedFiles.length
          ? [`Failed to upload ${failedFiles.length} file(s): ${failedFiles.join(', ')}.`]
          : [];
      };

      const handleDroppedFiles = async selectedFiles => {
        const files = Array.from(selectedFiles || []);
        if (!files.length || isUploadDisabled) {
          return;
        }

        const mediaFiles = files.filter(file => getPostMediaKind(file));
        const documentFiles = files.filter(isValidDocumentFile);
        const unsupportedFiles = files.filter(
          file => !getPostMediaKind(file) && !isValidDocumentFile(file)
        );

        setDropUploadError(null);
        setIsDropUploading(true);

        try {
          const uploadErrors = [
            ...(unsupportedFiles.length
              ? [
                  `${unsupportedFiles
                    .map(file => file.name)
                    .join(', ')}: only images, videos, PDF, DOC, and DOCX files are supported.`,
                ]
              : []),
            ...(await uploadDroppedMedia(mediaFiles)),
            ...(await uploadDroppedDocuments(documentFiles)),
          ];

          setDropUploadError(uploadErrors.length ? uploadErrors.join(' ') : null);
        } catch (error) {
          console.error('Composer drop upload failed:', error);
          setDropUploadError('An unexpected error occurred during upload. Please try again.');
        } finally {
          setIsDropUploading(false);
        }
      };

      const preventDragDefaults = event => {
        event.preventDefault();
        event.stopPropagation();
      };

      const handleDragEnter = event => {
        preventDragDefaults(event);
        if (isUploadDisabled) {
          return;
        }
        dragCounterRef.current += 1;
        if (event.dataTransfer?.items?.length) {
          setIsDragActive(true);
        }
      };

      const handleDragOver = event => {
        preventDragDefaults(event);
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = isUploadDisabled ? 'none' : 'copy';
        }
      };

      const handleDragLeave = event => {
        preventDragDefaults(event);
        if (isUploadDisabled) {
          return;
        }
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
          dragCounterRef.current = 0;
          setIsDragActive(false);
        }
      };

      const handleDrop = event => {
        preventDragDefaults(event);
        const { dataTransfer } = event;
        dragCounterRef.current = 0;
        setIsDragActive(false);
        handleDroppedFiles(dataTransfer?.files);
        dataTransfer?.clearData?.();
      };

      const isSubmitDisabled =
        isButtonDisabled ||
        limitExceed ||
        !isOnline ||
        isDropUploading ||
        submitting ||
        requestCreateComment;

      return (
        <Form
          onSubmit={async values => {
            if (submitInProgressRef.current) {
              return;
            }

            submitInProgressRef.current = true;
            setSubmitError(null); // Clear any previous errors
            try {
              const result = await handleSubmit(values);
              // Only reset the form if the comment was successfully created
              if (result && !result.error) {
                form.reset();
              } else {
                setSubmitError('Failed to post comment. Please try again.');
              }
            } finally {
              submitInProgressRef.current = false;
            }
          }}
        >
          <div
            className={classNames(css.simpleCommentContainer, {
              [css.simpleCommentContainerDragActive]: isDragActive,
            })}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className={css.imageContainer}>
              <div className={css.imageWrapper}>
                {current_avatar_url ? (
                  <ResponsiveImage imagewithURL={current_avatar_url} />
                ) : (
                  <div className={css.nameWrapper}>{abbreviateName(currentUserName)}</div>
                )}
                {currentUserHasStripeConnected ? (
                  <div className={css.verfiedIcon}>
                    <IconCollection name="profile_badge_stripe" />
                  </div>
                ) : currentUserHasActiveSubscription ? (
                  <div className={css.verfiedIcon}>
                    <IconCollection name="profile_badge_elephant" />
                  </div>
                ) : null}
              </div>
            </div>
            <div className={css.inputText}>
              <div className={css.suggestField}>
                <CircleBlockEditor
                  currentUser={currentUser}
                  handleCloseFeedModal={handleCloseFeedModal}
                  onSetElephantVerificationModal={onSetElephantVerificationModal}
                  editorState={editorState}
                  setEditorState={handleEditorChange}
                  advancedSearchData={advancedSearchData}
                  onAdvancedSearch={onAdvancedSearch}
                  replyCommentPlaceHolder={
                    currentBlockType == UNORDER_LIST
                      ? ''
                      : replyCommentPlaceHolder || commentPlaceHolder
                  }
                  currentBlockType={currentBlockType}
                  setLimitExceed={setLimitExceed}
                />
              </div>
              <br />
              {limitExceed ? (
                <span className={css.errorText}>
                  <FormattedMessage id="CreatePost.errorText" />
                </span>
              ) : null}
              {submitError ? <span className={css.errorText}>{submitError}</span> : null}
              {isDropUploading ? (
                <div className={css.dropUploadStatus}>
                  <IconSpinner type={true} />
                  <span>Uploading dropped files...</span>
                </div>
              ) : null}
              {dropUploadError ? <span className={css.errorText}>{dropUploadError}</span> : null}
              {/* render images */}
              <div className={css.uploadcontainerScroll}>
                {values?.imagefiles ? (
                  <div className={css.imageuploadedWrapper}>
                    {Array.isArray(values?.imagefiles)
                      ? values?.imagefiles.map((file, index) => {
                          const imageSrc = typeof file?.file === 'string' ? file.file : null;
                          const imageKey = file.signed_id || imageSrc || index;
                          return (
                            <div key={imageKey} className={css.mediaItemWrapper}>
                              <button
                                type="button"
                                className={css.removeMediaButton}
                                onClick={() => {
                                  const updatedFiles = values.imagefiles.filter(f =>
                                    file.signed_id ? f.signed_id !== file.signed_id : f !== file
                                  );
                                  const updatedImages = values.image?.filter(img =>
                                    file.signed_id ? img.signed_id !== file.signed_id : img !== file
                                  );
                                  form.change(
                                    'imagefiles',
                                    updatedFiles.length > 0 ? updatedFiles : undefined
                                  );
                                  form.change(
                                    'image',
                                    updatedImages?.length > 0 ? updatedImages : undefined
                                  );
                                }}
                              >
                                ×
                              </button>
                              {imageSrc ? (
                                <img
                                  src={imageSrc}
                                  className={css.uploadImageContainer}
                                  alt={file.name || ''}
                                />
                              ) : (
                                <ImageFromFile
                                  id={file.file.name}
                                  className={css.uploadImageContainer}
                                  file={file.file}
                                  aspectWidth={1}
                                  aspectHeight={1}
                                />
                              )}
                            </div>
                          );
                        })
                      : null}
                  </div>
                ) : null}
                {/* render gifs */}
                {values?.giffiles ? (
                  <div className={css.gifuploadWrapper}>
                    {Array.isArray(values?.giffiles)
                      ? values?.giffiles.map((file, index) => {
                          const gifSrc = typeof file?.file === 'string' ? file.file : file?.file;
                          return (
                            <div
                              key={file.signed_id || gifSrc || index}
                              className={css.mediaItemWrapper}
                            >
                              <button
                                type="button"
                                className={css.removeMediaButton}
                                onClick={() => {
                                  const updatedFiles = values.giffiles.filter(
                                    (_, i) => i !== index
                                  );
                                  const updatedGifs = values.gifs?.filter((_, i) => i !== index);
                                  form.change(
                                    'giffiles',
                                    updatedFiles.length > 0 ? updatedFiles : undefined
                                  );
                                  form.change(
                                    'gifs',
                                    updatedGifs?.length > 0 ? updatedGifs : undefined
                                  );
                                }}
                              >
                                ×
                              </button>
                              <img src={gifSrc} alt="gif" />
                            </div>
                          );
                        })
                      : null}
                  </div>
                ) : null}

                {/* render videos */}
                {values?.videofiles ? (
                  <div className={css.videoWrapper}>
                    {Array.isArray(values?.videofiles)
                      ? values?.videofiles.map((file, index) => {
                          const videoSrc = typeof file?.file === 'string' ? file.file : file?.file;
                          return (
                            <div
                              key={file.signed_id || videoSrc || index}
                              className={css.mediaItemWrapper}
                            >
                              <button
                                type="button"
                                className={css.removeMediaButton}
                                onClick={() => {
                                  const updatedFiles = values.videofiles.filter(
                                    (_, i) => i !== index
                                  );
                                  const updatedVideos = values.video?.filter((_, i) => i !== index);
                                  form.change(
                                    'videofiles',
                                    updatedFiles.length > 0 ? updatedFiles : undefined
                                  );
                                  form.change(
                                    'video',
                                    updatedVideos?.length > 0 ? updatedVideos : undefined
                                  );
                                }}
                              >
                                ×
                              </button>
                              <video src={videoSrc} poster={file.poster} loop autoPlay muted />
                            </div>
                          );
                        })
                      : null}
                  </div>
                ) : null}
                {/* render documents */}
                {values?.documentsfiles ? (
                  <div className={css.fileUplaodedWrapper}>
                    {Array.isArray(values?.documentsfiles)
                      ? values?.documentsfiles.map((file, index) => {
                          return (
                            <div
                              className={css.fileUploaded}
                              key={file.signed_id || file.name || index}
                            >
                              <button
                                type="button"
                                className={css.removeDocButton}
                                onClick={() => {
                                  const updatedFiles = values.documentsfiles.filter(
                                    (_, i) => i !== index
                                  );
                                  const updatedDocs = values.document?.filter(
                                    (_, i) => i !== index
                                  );
                                  form.change(
                                    'documentsfiles',
                                    updatedFiles.length > 0 ? updatedFiles : undefined
                                  );
                                  form.change(
                                    'document',
                                    updatedDocs?.length > 0 ? updatedDocs : undefined
                                  );
                                }}
                              >
                                ×
                              </button>
                              <span>
                                <ProfileIconCard type="document" />
                                {file.name || file.filename || 'Document'}
                              </span>
                            </div>
                          );
                        })
                      : null}
                  </div>
                ) : null}
              </div>
              <div
                className={classNames(css.row, {
                  [css.editRow]: isEditMode,
                })}
              >
                <div className={css.bottomFunctionMenu}>
                  <div className={css.leftIconSection}>
                    <div className={css.documentUpload}>
                      {/* //isEditorOptionOpen, setIsEditorOptionOpen */}
                      <OutsideClickHandler
                        onOutsideClick={() => isEditorOptionOpen && setIsEditorOptionOpen(false)}
                      >
                        <label className={css.uplaodIcon} onClick={toggleDropdown}>
                          <ProfileIconCard type="add_file" />
                        </label>
                        <div className={css.editDropdown}>
                          {isEditorOptionOpen ? (
                            <EditorOptionDropdown
                              toggleDropdown={toggleDropdown}
                              toggleBlockType={toggleBlockType}
                              toggleInlineStyle={toggleInlineStyle}
                              currentBlockType={currentBlockType}
                              currentInlineStyle={currentInlineStyle}
                            />
                          ) : null}
                        </div>
                      </OutsideClickHandler>
                    </div>
                    <div className={css.inputField}>
                      <button
                        type="button"
                        aria-label="Attach file"
                        className={`${css.uplaodIcon} ${!isOnline ? css.disabledIcon : ''}`}
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (isOnline) onDocumentClick();
                        }}
                        disabled={!isOnline}
                      >
                        <ProfileIconCard type="document_uplaod" />
                      </button>
                      {isDocumentModal && isOnline ? (
                        <FileUploadModal
                          isDocumentModal={isDocumentModal}
                          onDocumentClick={onDocumentClick}
                          form={form}
                          values={values}
                          requestDirectUpload={requestDirectUpload}
                          usePortal
                        />
                      ) : null}
                    </div>

                    <div className={css.gifSection}>
                      <button
                        type="button"
                        aria-label="Add GIF"
                        className={`${css.uplaodIcon} ${!isOnline ? css.disabledIcon : ''}`}
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (isOnline) onGiphyChange();
                        }}
                        disabled={!isOnline}
                      >
                        <ProfileIconCard type="gif" />
                      </button>

                      {isGiphyOpen && isOnline ? (
                        <GifUploadModal
                          isGiphyOpen={isGiphyOpen}
                          onGiphyChange={onGiphyChange}
                          form={form}
                          values={values}
                          requestDirectUpload={requestDirectUpload}
                          usePortal
                        />
                      ) : null}
                    </div>
                    <div className={css.inputField}>
                      <button
                        type="button"
                        aria-label="Add image"
                        className={`${css.uplaodIcon} ${!isOnline ? css.disabledIcon : ''}`}
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (isOnline) onImageClick();
                        }}
                        disabled={!isOnline}
                      >
                        <ProfileIconCard type="image" />
                      </button>
                      {isImageModal && isOnline ? (
                        <ImageUploadModal
                          isImageModal={isImageModal}
                          onImageClick={onImageClick}
                          form={form}
                          values={values}
                          requestDirectUpload={requestDirectUpload}
                          usePortal
                        />
                      ) : null}
                    </div>
                    <div className={css.inputField}>
                      <label
                        htmlFor="fileInput"
                        className={`${css.uplaodIcon} ${!isOnline ? css.disabledIcon : ''}`}
                        onClick={isOnline ? onVideoClick : undefined}
                      >
                        <ProfileIconCard type="video" />
                      </label>
                      {isOnline && (
                        <VideoUploadModal
                          isVideoUpload={isVideoUpload}
                          onVideoClick={onVideoClick}
                          form={form}
                          values={values}
                          requestDirectUpload={requestDirectUpload}
                        />
                      )}
                    </div>
                    <OutsideClickHandler onOutsideClick={() => isEmojiOpen && onEmojiClick(false)}>
                      <div className={css.emojiSection}>
                        <div
                          className={`${css.uplaodIcon} ${!isOnline ? css.disabledIcon : ''}`}
                          onClick={isOnline ? () => onEmojiClick(true) : undefined}
                        >
                          <ProfileIconCard type="emoji" />
                        </div>

                        <div className={css.emojiPick}>
                          {isEmojiOpen && isOnline ? (
                            <EmojiPicker onEmojiClick={handleEmoji} />
                          ) : null}
                        </div>
                      </div>
                    </OutsideClickHandler>
                    {/* <div>
                      <ProfileIconCard type="marketplace" />
                    </div> */}
                  </div>
                </div>
                <div className={css.commentActionButtons}>
                  {isEditMode ? (
                    <button
                      type="button"
                      className={css.cancelCommentButton}
                      onClick={onCancelEdit}
                    >
                      Cancel
                    </button>
                  ) : null}
                  <Button
                    className={css.postButton}
                    type="submit"
                    inProgress={submitting || requestCreateComment}
                    disabled={isSubmitDisabled}
                  >
                    {submitLabel}
                  </Button>
                </div>
              </div>
              {!isOnline && (
                <div className={css.errorMessage}>
                  <FormattedMessage
                    id="CreatePost.noInternetConnection"
                    defaultMessage="No internet connection. Please check your network."
                  />
                </div>
              )}
            </div>
          </div>
        </Form>
      );
    }}
  />
);

export default AddCommentForm;